import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New Lead' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'negotiating', label: 'Negotiating' },
  { value: 'won', label: 'Converted' },
  { value: 'lost', label: 'Lost' },
  { value: 'on_hold', label: 'On Hold' },
];

const COMPANY_TYPE_OPTIONS = [
  { value: 'commercial', label: 'Commercial' },
  { value: 'residential', label: 'Residential' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'government', label: 'Government' },
  { value: 'nonprofit', label: 'Non-Profit' },
  { value: 'other', label: 'Other' },
];

const SOURCE_OPTIONS = [
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'trade_show', label: 'Trade Show' },
  { value: 'linkedin', label: 'Social Media' },
  { value: 'email_campaign', label: 'Email Campaign' },
  { value: 'advertisement', label: 'Advertisement' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FORM = {
  companyName: '', companyType: 'other', status: 'new', source: 'other',
  website: '', address: '', city: '', state: '', zipCode: '',
  estimatedValue: '', probability: '0', notes: '',
  contactFirstName: '', contactLastName: '', contactTitle: '',
  contactEmail: '', contactPhone: ''
};

const ProspectFormModal = ({ isOpen, onClose, onSave, prospect }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (prospect) {
      const primaryContact = prospect?.prospectContacts?.find(c => c?.isPrimary) || prospect?.prospectContacts?.[0];
      setFormData({
        companyName: prospect?.companyName || '',
        companyType: prospect?.companyType || 'other',
        status: prospect?.status || 'new',
        source: prospect?.source || 'other',
        website: prospect?.website || '',
        address: prospect?.address || '',
        city: prospect?.city || '',
        state: prospect?.state || '',
        zipCode: prospect?.zipCode || '',
        estimatedValue: prospect?.estimatedValue || '',
        probability: prospect?.probability?.toString() || '0',
        notes: prospect?.notes || '',
        contactFirstName: primaryContact?.firstName || '',
        contactLastName: primaryContact?.lastName || '',
        contactTitle: primaryContact?.title || '',
        contactEmail: primaryContact?.email || '',
        contactPhone: primaryContact?.phone || '',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [prospect, isOpen]);

  const validate = () => {
    const errs = {};
    if (!formData?.companyName?.trim()) errs.companyName = 'Company name is required';
    if (!formData?.contactFirstName?.trim()) errs.contactFirstName = 'First name is required';
    if (!formData?.contactLastName?.trim()) errs.contactLastName = 'Last name is required';
    if (formData?.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.contactEmail)) {
      errs.contactEmail = 'Enter a valid email address';
    }
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {prospect ? 'Edit Prospect' : 'Add New Prospect'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Company Info */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Building2" size={16} className="text-accent" />
              Company Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Input
                  label="Company Name"
                  required
                  value={formData?.companyName}
                  onChange={e => handleChange('companyName', e?.target?.value)}
                  error={errors?.companyName}
                  placeholder="Enter company name"
                />
              </div>
              <Select
                label="Company Type"
                options={COMPANY_TYPE_OPTIONS}
                value={formData?.companyType}
                onChange={v => handleChange('companyType', v)}
              />
              <Select
                label="How Did We Find Them?"
                options={SOURCE_OPTIONS}
                value={formData?.source}
                onChange={v => handleChange('source', v)}
              />
              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={formData?.status}
                onChange={v => handleChange('status', v)}
              />
              <Input
                label="Website"
                value={formData?.website}
                onChange={e => handleChange('website', e?.target?.value)}
                placeholder="https://"
              />
              <Input
                label="Estimated Value ($)"
                type="number"
                value={formData?.estimatedValue}
                onChange={e => handleChange('estimatedValue', e?.target?.value)}
                placeholder="0.00"
              />
              <Input
                label="Win Probability (%)"
                type="number"
                value={formData?.probability}
                onChange={e => handleChange('probability', e?.target?.value)}
                placeholder="0-100"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="md:col-span-1">
                <Input
                  label="City"
                  value={formData?.city}
                  onChange={e => handleChange('city', e?.target?.value)}
                  placeholder="City"
                />
              </div>
              <Input
                label="State"
                value={formData?.state}
                onChange={e => handleChange('state', e?.target?.value)}
                placeholder="State"
              />
              <Input
                label="ZIP Code"
                value={formData?.zipCode}
                onChange={e => handleChange('zipCode', e?.target?.value)}
                placeholder="ZIP"
              />
            </div>
          </div>

          {/* Primary Contact */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="User" size={16} className="text-accent" />
              Primary Contact
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                required
                value={formData?.contactFirstName}
                onChange={e => handleChange('contactFirstName', e?.target?.value)}
                error={errors?.contactFirstName}
                placeholder="First name"
              />
              <Input
                label="Last Name"
                required
                value={formData?.contactLastName}
                onChange={e => handleChange('contactLastName', e?.target?.value)}
                error={errors?.contactLastName}
                placeholder="Last name"
              />
              <Input
                label="Title / Role"
                value={formData?.contactTitle}
                onChange={e => handleChange('contactTitle', e?.target?.value)}
                placeholder="e.g. VP of Operations"
              />
              <Input
                label="Email"
                type="email"
                value={formData?.contactEmail}
                onChange={e => handleChange('contactEmail', e?.target?.value)}
                error={errors?.contactEmail}
                placeholder="email@company.com"
              />
              <Input
                label="Phone"
                value={formData?.contactPhone}
                onChange={e => handleChange('contactPhone', e?.target?.value)}
                placeholder="555-123-4567"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-2">Notes</label>
            <textarea
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[80px] resize-y"
              value={formData?.notes}
              onChange={e => handleChange('notes', e?.target?.value)}
              placeholder="Additional notes about this prospect..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={saving} iconName="Save" iconPosition="left">
              {prospect ? 'Save Changes' : 'Add Prospect'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProspectFormModal;
